<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import {
  Activity,
  AudioLines,
  Bot,
  Cpu,
  ExternalLink,
  LayoutDashboard,
  KeyRound,
  MessageSquareText,
  RefreshCw,
  Settings,
  SlidersHorizontal,
  Volume2,
  X,
} from "lucide-vue-next";
import { api, ApiClientError } from "./services/api";
import { config, saveConfig, type ConsoleConfig } from "./state/config";
import type { Agent, AgentSummary, Health } from "./types";
import DashboardView from "./views/DashboardView.vue";
import ChatView from "./views/ChatView.vue";
import RealtimeView from "./views/RealtimeView.vue";
import TtsView from "./views/TtsView.vue";
import DevicesView from "./views/DevicesView.vue";
import AgentsView from "./views/AgentsView.vue";
import ClientsView from "./views/ClientsView.vue";
import ServiceConfigView from "./views/ServiceConfigView.vue";

type ViewKey = "dashboard" | "chat" | "realtime" | "tts" | "devices" | "agents" | "clients" | "service-config";

const navItems = [
  { key: "dashboard" as const, label: "运行总览", icon: LayoutDashboard },
  { key: "chat" as const, label: "智能问答", icon: MessageSquareText },
  { key: "realtime" as const, label: "实时语音", icon: AudioLines },
  { key: "tts" as const, label: "语音合成", icon: Volume2 },
  { key: "devices" as const, label: "设备控制", icon: Cpu },
  { key: "agents" as const, label: "智能体", icon: Bot },
  { key: "clients" as const, label: "客户端", icon: KeyRound },
  { key: "service-config" as const, label: "服务配置", icon: SlidersHorizontal },
];

const activeView = ref<ViewKey>("dashboard");
const health = ref<Health>();
const managedAgents = ref<AgentSummary[]>([]);
const clientAgents = ref<AgentSummary[]>([]);
const loading = ref(false);
const globalError = ref("");
const clientAccessError = ref("");
const settingsOpen = ref(false);
const draftConfig = ref<ConsoleConfig>({ ...config });
let timer: number | undefined;

const currentTitle = computed(() => navItems.find((item) => item.key === activeView.value)?.label ?? "AI 能力验证台");
const backendReady = computed(() => health.value?.status === "ready");
const isClientTestView = computed(() => activeView.value === "chat" || activeView.value === "realtime" || activeView.value === "tts");

function toSummary(agent: Agent): AgentSummary {
  return {
    slug: agent.slug,
    name: agent.name,
    description: agent.description,
    capabilities: {
      text: agent.textEnabled,
      tts: agent.ttsEnabled,
      realtime: agent.realtimeEnabled,
      deviceControl: agent.deviceControlEnabled,
    },
  };
}

function clientAuthMessage(reason: unknown): string {
  if (reason instanceof ApiClientError && (reason.code === "CLIENT_KEY_INVALID" || reason.code === "CLIENT_KEY_REQUIRED")) {
    return "当前管理台标签页的客户端 Key 无效或已失效，请更新后再测试客户端能力。";
  }
  return reason instanceof Error ? reason.message : "客户端授权智能体加载失败";
}

async function refresh(): Promise<void> {
  loading.value = true;
  globalError.value = "";
  clientAccessError.value = "";
  const [healthResult, adminAgentResult, clientAgentResult] = await Promise.allSettled([
    api.health(), api.adminAgents(), api.agents(),
  ]);
  if (healthResult.status === "fulfilled") health.value = healthResult.value;
  else globalError.value = healthResult.reason instanceof Error ? healthResult.reason.message : "后台连接失败";
  if (adminAgentResult.status === "fulfilled") {
    managedAgents.value = adminAgentResult.value.filter((agent) => agent.isEnabled).map(toSummary);
  } else if (!globalError.value) {
    globalError.value = adminAgentResult.reason instanceof Error ? adminAgentResult.reason.message : "管理数据加载失败";
  }
  if (clientAgentResult.status === "fulfilled") clientAgents.value = clientAgentResult.value;
  else {
    clientAgents.value = [];
    clientAccessError.value = clientAuthMessage(clientAgentResult.reason);
  }
  loading.value = false;
}

function openSettings(): void {
  draftConfig.value = { ...config };
  settingsOpen.value = true;
}

function submitSettings(): void {
  saveConfig(draftConfig.value);
  settingsOpen.value = false;
  void refresh();
}

onMounted(() => {
  void refresh();
  timer = window.setInterval(() => void refresh(), 15_000);
});
onBeforeUnmount(() => timer && window.clearInterval(timer));
</script>

<template>
  <div class="app-shell">
    <aside class="sidebar">
      <div class="brand-block">
        <div class="brand-mark"><Activity :size="20" /></div>
        <div>
          <strong>AI 管理台</strong>
          <span>matttest admin</span>
        </div>
      </div>

      <nav class="main-nav" aria-label="主导航">
        <button
          v-for="item in navItems"
          :key="item.key"
          type="button"
          :class="['nav-button', { active: activeView === item.key }]"
          @click="activeView = item.key"
        >
          <component :is="item.icon" :size="18" />
          <span>{{ item.label }}</span>
        </button>
      </nav>

      <div class="sidebar-status">
        <span :class="['status-dot', backendReady ? 'ok' : 'bad']" />
        <div>
          <strong>{{ backendReady ? "后台已连接" : "后台未就绪" }}</strong>
          <span>{{ managedAgents.length }} 个可用智能体</span>
        </div>
      </div>
    </aside>

    <section class="workspace">
      <header class="topbar">
        <div>
          <span class="topbar-kicker">后台管理</span>
          <h1>{{ currentTitle }}</h1>
        </div>
        <div class="topbar-actions">
          <span v-if="globalError" class="inline-error">{{ globalError }}</span>
          <a class="icon-button" href="/client" title="打开交互终端"><ExternalLink :size="18" /></a>
          <button class="icon-button" type="button" title="刷新" :disabled="loading" @click="refresh">
            <RefreshCw :size="18" :class="{ spinning: loading }" />
          </button>
          <button class="icon-button" type="button" title="连接设置" @click="openSettings">
            <Settings :size="18" />
          </button>
        </div>
      </header>

      <main class="main-content">
        <div v-if="clientAccessError && isClientTestView" class="client-access-warning">
          <KeyRound :size="18" />
          <div><strong>客户端测试凭证需要更新</strong><span>{{ clientAccessError }}</span></div>
          <button class="button secondary" type="button" @click="openSettings">更新 Key</button>
        </div>
        <DashboardView v-if="activeView === 'dashboard'" :health="health" :agents="managedAgents" />
        <ChatView v-else-if="activeView === 'chat'" :agents="clientAgents" />
        <RealtimeView v-else-if="activeView === 'realtime'" :agents="clientAgents" />
        <TtsView v-else-if="activeView === 'tts'" :agents="clientAgents" />
        <DevicesView v-else-if="activeView === 'devices'" />
        <AgentsView v-else-if="activeView === 'agents'" :on-agent-change="refresh" />
        <ClientsView v-else-if="activeView === 'clients'" />
        <ServiceConfigView v-else :on-config-change="refresh" />
      </main>
    </section>

    <div v-if="settingsOpen" class="modal-backdrop" @click.self="settingsOpen = false">
      <section class="modal-panel" role="dialog" aria-modal="true" aria-labelledby="settings-title">
        <div class="modal-header">
          <div>
            <span class="eyebrow">连接</span>
            <h2 id="settings-title">后台设置</h2>
          </div>
          <button class="icon-button" type="button" title="关闭" @click="settingsOpen = false"><X :size="18" /></button>
        </div>
        <form class="form-stack" @submit.prevent="submitSettings">
          <label>
            <span>服务地址</span>
            <input v-model="draftConfig.baseUrl" placeholder="留空使用本地代理" />
          </label>
          <label>
            <span>客户端密钥</span>
            <input v-model="draftConfig.clientKey" type="password" autocomplete="off" />
          </label>
          <label>
            <span>管理员密钥</span>
            <input v-model="draftConfig.adminKey" type="password" autocomplete="off" />
          </label>
          <div class="modal-actions">
            <button class="button secondary" type="button" @click="settingsOpen = false">取消</button>
            <button class="button primary" type="submit">保存连接</button>
          </div>
        </form>
      </section>
    </div>
  </div>
</template>
